import React, { useState, useEffect, useCallback } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import CircularProgress from "@mui/material/CircularProgress";
import Chip from "@mui/material/Chip";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import Alert from "@mui/material/Alert";
import Checkbox from "@mui/material/Checkbox";

import { getCalendarYears, getRollForwardPreview, applyRollForward } from "../services/seasonSequenceApi";

// How many years forward the target selector offers (source+1 .. source+FORWARD).
const FORWARD = 3;

// Annual roll-forward: create next year's Franchise Calendars from a source year by
// cloning each SELECTED franchise's source calendar. Source year comes from the years
// that actually have calendars; target is a constrained forward year (default
// source + 1) to prevent typos like 202 / 2207.
//
// Selective: only franchises ticked in the preview are created. The selection starts
// with the franchise chosen on the page (when it is eligible to create), otherwise
// empty -- a bulk run is an explicit "select all". The server re-validates the
// selection against its own preview at apply time.
export default function RollForwardDialog(props) {
  const { open, onClose, comcodes = [], onApplied, selectedComCode = null } = props;

  const [calendarYears, setCalendarYears] = useState([]);
  const [sourceYear, setSourceYear] = useState("");
  const [targetYear, setTargetYear] = useState("");

  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [previewError, setPreviewError] = useState(null);

  const [selected, setSelected] = useState([]);

  const [applying, setApplying] = useState(false);
  const [applyError, setApplyError] = useState(null);
  const [result, setResult] = useState(null);

  const franchiseName = useCallback(
    (comCode) => {
      const f = comcodes.find((c) => c.comcode === comCode);
      return f && f.franchise_name ? f.franchise_name : comCode;
    },
    [comcodes]
  );

  // On open: load source-year options, default source = newest, target = source + 1.
  useEffect(() => {
    if (!open) return;
    setResult(null);
    setApplyError(null);
    setPreview(null);
    setPreviewError(null);
    setSelected([]);
    getCalendarYears()
      .then((res) => {
        const years = res.years || [];
        setCalendarYears(years);
        if (years.length) {
          const newest = Math.max(...years);
          setSourceYear(newest);
          setTargetYear(newest + 1);
        }
      })
      .catch(() => setCalendarYears([]));
  }, [open]);

  const sourceNum = Number(sourceYear);
  const targetNum = Number(targetYear);
  const yearsValid = String(sourceYear) !== "" && String(targetYear) !== "";

  // Auto-preview on a valid source/target pair (debounced so a source change that
  // also resets the target doesn't double-fire). Suppressed once a result shows.
  useEffect(() => {
    if (!open || !yearsValid || result) return;
    const t = setTimeout(() => {
      setLoadingPreview(true);
      setPreviewError(null);
      getRollForwardPreview(sourceNum, targetNum)
        .then((res) => {
          setPreview(res);
          setSelected(initialSelection(res, selectedComCode));
        })
        .catch((err) => { setPreview(null); setSelected([]); setPreviewError(err.message); })
        .finally(() => setLoadingPreview(false));
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, sourceNum, targetNum, yearsValid, result]);

  const handleSourceChange = (e) => {
    const v = Number(e.target.value);
    setSourceYear(v);
    setTargetYear(v + 1);
    setResult(null);
  };

  const handleTargetChange = (e) => {
    setTargetYear(Number(e.target.value));
    setResult(null);
  };

  const handleApply = () => {
    setApplying(true);
    setApplyError(null);
    applyRollForward({ sourceTrackingYear: sourceNum, targetTrackingYear: targetNum, com_codes: selected })
      .then((res) => setResult(res))
      .catch((err) => setApplyError(err.message))
      .finally(() => setApplying(false));
  };

  const handleDone = () => {
    if (result && onApplied) onApplied({ targetTrackingYear: targetNum, result });
    onClose();
  };

  const continuity = preview ? preview.continuity : null;
  const manual = preview ? preview.needsManualSetup : null;
  const locked = applying || !!result;

  const eligibleCodes = continuity
    ? continuity.franchises.filter((f) => f.action === "create").map((f) => f.com_code)
    : [];
  const selectedCount = selected.length;
  const allEligibleSelected = eligibleCodes.length > 0 && selectedCount === eligibleCodes.length;

  const toggleOne = (comCode) => {
    setSelected((prev) => (prev.includes(comCode) ? prev.filter((c) => c !== comCode) : [...prev, comCode]));
  };
  const toggleAll = () => {
    setSelected(allEligibleSelected ? [] : eligibleCodes);
  };

  const sourceYearItems = [...calendarYears]
    .sort((a, b) => b - a)
    .map((y) => (
      <MenuItem key={y} value={y}>{y}</MenuItem>
    ));

  const targetYearItems = (Number.isInteger(sourceNum) ? [1, 2, 3].slice(0, FORWARD).map((n) => sourceNum + n) : [])
    .map((y) => (
      <MenuItem key={y} value={y}>{y}</MenuItem>
    ));

  const previewRows = continuity
    ? continuity.franchises.map((f) => (
        <TableRow key={f.com_code} hover={f.action === "create"}>
          <TableCell padding="checkbox">
            {f.action === "create" && (
              <Checkbox
                size="small"
                checked={selected.includes(f.com_code)}
                onChange={() => toggleOne(f.com_code)}
                disabled={locked}
                inputProps={{ "aria-label": `Create ${f.com_code}` }}
              />
            )}
          </TableCell>
          <TableCell>
            {franchiseName(f.com_code)}
            {f.source_bucket_count > 5 && (
              <Chip size="small" color="warning" variant="outlined" sx={{ ml: 1 }} label={`${f.source_bucket_count} buckets`} />
            )}
          </TableCell>
          <TableCell>
            {f.action === "create" ? (
              <Chip size="small" color="success" variant="outlined" label="Create" />
            ) : (
              <Chip size="small" variant="outlined" label="Already exists — skip" />
            )}
          </TableCell>
        </TableRow>
      ))
    : null;

  const manualRows = manual
    ? manual.franchises.map((f) => (
        <li key={f.com_code}>
          {franchiseName(f.com_code)} ({f.target_season_count} season{f.target_season_count === 1 ? "" : "s"})
        </li>
      ))
    : null;

  const errorItems = result
    ? result.errors.map((e) => (
        <li key={e.com_code}>{franchiseName(e.com_code)}: {e.message}</li>
      ))
    : null;

  const skippedItems = result
    ? result.skipped.map((sk) => (
        <li key={sk.com_code}>{franchiseName(sk.com_code)}: {sk.reason}</li>
      ))
    : null;

  return (
    <Dialog open={open} onClose={locked ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>Roll forward Franchise Calendars</DialogTitle>
      <DialogContent dividers>
        <DialogContentText sx={{ mb: 2 }}>
          Create next year's Franchise Calendars by cloning the selected source year. Existing target
          calendars are left unchanged.
        </DialogContentText>

        <Box sx={{ display: "flex", gap: 2, alignItems: "center", mb: 2, flexWrap: "wrap" }}>
          <FormControl size="small" sx={{ minWidth: 190 }} disabled={locked}>
            <InputLabel id="rf-source-label">Clone calendars from</InputLabel>
            <Select labelId="rf-source-label" label="Clone calendars from" value={sourceYear} onChange={handleSourceChange}>
              {sourceYearItems}
            </Select>
          </FormControl>
          <Typography aria-hidden>→</Typography>
          <FormControl size="small" sx={{ minWidth: 190 }} disabled={locked || String(sourceYear) === ""}>
            <InputLabel id="rf-target-label">Create calendars for</InputLabel>
            <Select labelId="rf-target-label" label="Create calendars for" value={targetYear} onChange={handleTargetChange}>
              {targetYearItems}
            </Select>
          </FormControl>
        </Box>

        {result ? (
          <Box>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>Rolled forward {sourceNum} → {targetNum}</Typography>
            <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
              <Chip color="success" variant="outlined" label={`Created: ${result.summary.created}`} />
              <Chip variant="outlined" label={`Skipped: ${result.summary.skipped}`} />
              <Chip color={result.summary.errors > 0 ? "error" : "default"} variant="outlined" label={`Errors: ${result.summary.errors}`} />
            </Stack>
            {result.skipped.length > 0 && <Box component="ul" sx={{ mt: 0 }}>{skippedItems}</Box>}
            {result.errors.length > 0 && <Box component="ul" sx={{ mt: 0 }}>{errorItems}</Box>}
          </Box>
        ) : (
          <>
            {applyError && <Alert severity="error" sx={{ mb: 2 }}>{applyError}</Alert>}
            {previewError && <Alert severity="error" sx={{ mb: 2 }}>{previewError}</Alert>}
            {loadingPreview && <CircularProgress size={24} />}
            {!loadingPreview && continuity && (
              <>
                <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                  <Chip color="success" variant="outlined" label={`Eligible: ${continuity.summary.to_create}`} />
                  <Chip color="primary" label={`Selected: ${selectedCount}`} />
                  <Chip variant="outlined" label={`Skip: ${continuity.summary.to_skip}`} />
                  <Chip
                    color={manual && manual.summary.count > 0 ? "warning" : "default"}
                    variant="outlined"
                    label={`Manual setup: ${manual ? manual.summary.count : 0}`}
                  />
                </Stack>
                <Box sx={{ maxHeight: 320, overflow: "auto", border: 1, borderColor: "divider", borderRadius: 1 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox">
                          <Checkbox
                            size="small"
                            checked={allEligibleSelected}
                            indeterminate={selectedCount > 0 && !allEligibleSelected}
                            onChange={toggleAll}
                            disabled={locked || eligibleCodes.length === 0}
                            inputProps={{ "aria-label": "Select all eligible franchises" }}
                          />
                        </TableCell>
                        <TableCell>Franchise</TableCell>
                        <TableCell>{targetNum} status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>{previewRows}</TableBody>
                  </Table>
                </Box>

                {manual && manual.summary.count > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" color="warning.main">
                      Needs manual setup — {manual.summary.count} franchise{manual.summary.count === 1 ? "" : "s"} with{" "}
                      {targetNum} seasons but no {sourceNum} calendar to clone:
                    </Typography>
                    <Box component="ul" sx={{ mt: 0.5 }}>{manualRows}</Box>
                  </Box>
                )}
              </>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        {result ? (
          <Button variant="contained" onClick={handleDone}>Done</Button>
        ) : (
          <>
            <Button onClick={onClose} disabled={applying}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleApply}
              disabled={applying || !continuity || selectedCount === 0 || !yearsValid}
            >
              {applying ? "Creating…" : `Create ${selectedCount} calendar${selectedCount === 1 ? "" : "s"}`}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}

// Initial selection for a fresh preview: just the page's franchise when it is eligible
// to create in this preview; otherwise nothing.
export function initialSelection(preview, selectedComCode) {
  if (!preview || !preview.continuity || !selectedComCode) return [];
  const match = preview.continuity.franchises.find(
    (f) => f.com_code === selectedComCode && f.action === "create"
  );
  return match ? [match.com_code] : [];
}
